/**
 * TroiaSidebar - Sidebar nativa com tabs + search + filtros + lista
 * Referência: ChatSidebarLeft (tabs border-b-2, collapse w-96/w-16)
 * F80: tooltips via Tooltip shared · F17-22: busca via ToolbarSearch shared
 */

import React, { useRef, useCallback, useState } from 'react';
import type { ConversationResponse } from '@troia-v3/types';
import type { EmailFolderType } from '../../shared/types/email.types';
import { TroiaEmailListItem } from './TroiaEmailListItem';
import { TroiaEmptyState } from './TroiaEmptyState';
import { ActionSheet, type ActionSheetItem, Tooltip, ToolbarSearch } from '@/shared/components/ui';
import { HEADER_HEIGHT } from '@/shared/constants/layout';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
type SidebarTab = 'inbox' | 'sent' | 'drafts' | 'spam' | 'trash' | 'archive';

interface TabDef {
  id: SidebarTab;
  label: string;
  icon: IconName;
  folderType: EmailFolderType;
}

const TABS: TabDef[] = [
  { id: 'inbox', label: 'Caixa de entrada', icon: 'inbox', folderType: 'inbox' },
  { id: 'sent', label: 'Enviados', icon: 'send', folderType: 'sent' },
  // `.em-item[data-fold="draft"]` — Rascunhos como 3ª pasta (mockup email.html:439)
  { id: 'drafts', label: 'Rascunhos', icon: 'file-text', folderType: 'drafts' },
  { id: 'spam', label: 'Spam', icon: 'alert-octagon', folderType: 'spam' },
  { id: 'trash', label: 'Lixeira', icon: 'trash', folderType: 'trash' },
  { id: 'archive', label: 'Arquivados', icon: 'archive', folderType: 'archive' },
];

interface TroiaSidebarProps {
  activeTab: SidebarTab;
  onChangeTab: (tab: SidebarTab) => void;
  conversations: ConversationResponse[];
  selectedId?: string;
  onSelect: (id: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Rail auto-colapsado por container query (esconde o botão de toggle manual). */
  disableToggle?: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onCompose: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  isLoading?: boolean;
  unreadCount?: number;
  folderCounts?: Record<string, number>;
  onNavigateFolder: (folder: EmailFolderType) => void;
  showOnlyStarred?: boolean;
  onToggleStarred?: () => void;
  onRefresh?: () => void;
}

export const TroiaSidebar: React.FC<TroiaSidebarProps> = ({
  activeTab,
  onChangeTab,
  conversations,
  selectedId,
  onSelect,
  collapsed,
  onToggleCollapse,
  disableToggle = false,
  searchQuery,
  onSearchChange,
  onCompose,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  unreadCount = 0,
  folderCounts,
  onNavigateFolder,
  showOnlyStarred = false,
  onToggleStarred,
  onRefresh,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // F80: tooltip shared (Radix) — wrap condicional (rail colapsado mostra tooltip)
  const withTooltip = (label: string, node: React.ReactElement, enabled = true) =>
    enabled ? (
      <Tooltip content={label} side="bottom">
        {node}
      </Tooltip>
    ) : (
      node
    );

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasNextPage) {
          fetchNextPage?.();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleTabClick = (tab: TabDef) => {
    onChangeTab(tab.id);
    onNavigateFolder(tab.folderType);
  };

  // Mobile: pastas viram um ActionSheet a partir do header da lista (o rail é
  // desktop-only). Os itens espelham 1:1 as pastas do rail + Favoritos.
  const [mobileFoldersOpen, setMobileFoldersOpen] = useState(false);

  const mobileFolderItems: ActionSheetItem[] = React.useMemo(() => {
    const items: ActionSheetItem[] = TABS.map((tab) => {
      const count = folderCounts?.[tab.id] || (tab.id === 'inbox' ? unreadCount : 0);
      return {
        key: tab.id,
        label: count > 0 ? `${tab.label} (${count > 99 ? '99+' : count})` : tab.label,
        icon: tab.icon,
        testId: `email-mobile-folder-${tab.id}`,
      };
    });
    if (onToggleStarred) {
      items.push({
        key: 'starred',
        label: 'Favoritos',
        icon: 'star',
        separatorBefore: true,
        testId: 'email-mobile-folder-starred',
      });
    }
    return items;
  }, [folderCounts, unreadCount, onToggleStarred]);

  return (
    <div className="flex h-full min-h-0 w-full lg:w-auto">
      {/* ═══ Rail de pastas `.em-folders` (mockup email.html): Escrever no topo +
          pastas 38px c/ badges; colapsa pra só-ícones. Favoritos = pasta (toggle).
          Mobile: escondida — pasta atual vira dropdown no header da lista. ═══ */}
      <div
        className="hidden lg:flex flex-col h-full min-h-0 border-r transition-all duration-300 flex-none"
        style={{
          width: collapsed ? 64 : 236,
          backgroundColor: 'var(--theme-sidebar-background)',
          borderColor: 'var(--theme-sidebar-border)',
        }}
        data-testid="email-folders"
      >
        {/* `.em-compose` Escrever (42px roxo) */}
        {withTooltip(
          'Escrever',
          <button
            onClick={onCompose}
            className="flex items-center justify-center gap-2 rounded-[10px] text-sm font-semibold text-white flex-none transition-colors"
            style={{
              margin: collapsed ? '14px 8px 8px' : '14px 12px 8px',
              height: 42,
              backgroundColor: 'var(--theme-button-primary)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
            }}
            data-testid="email-compose"
          >
            <Icon name="edit" className="w-4 h-4" />
            {!collapsed && 'Escrever'}
          </button>,
          collapsed
        )}

        {/* `.em-nav`: pastas */}
        <nav className="flex-1 overflow-y-auto" style={{ padding: '4px 8px 12px' }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id && !showOnlyStarred;
            const count = folderCounts?.[tab.id] || (tab.id === 'inbox' ? unreadCount : 0);
            return (
              <React.Fragment key={tab.id}>
                {withTooltip(
                  tab.label,
                  <button
                    onClick={() => handleTabClick(tab)}
                    className={`flex items-center w-full rounded-[9px] text-sm transition-colors ${collapsed ? 'justify-center' : 'gap-[11px] text-left'} ${isActive ? 'font-semibold' : 'font-medium'}`}
                    style={{
                      height: 38,
                      padding: collapsed ? 0 : '0 11px',
                      backgroundColor: isActive ? 'var(--theme-primary-background)' : 'transparent',
                      color: isActive ? 'var(--theme-primary)' : 'var(--theme-sidebar-text)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    data-testid={`email-folder-${tab.id}`}
                  >
                    <Icon name={tab.icon} className="w-4 h-4 flex-none" style={{ color: isActive ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }} />
                    {!collapsed && <span className="flex-1 min-w-0 truncate">{tab.label}</span>}
                    {!collapsed && count > 0 && (
                      <span
                        className="text-xs font-semibold flex-none"
                        style={{ color: isActive ? 'var(--theme-primary)' : 'var(--theme-card-muted, var(--theme-muted-text))', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                    {collapsed && count > 0 && (
                      <span
                        className="absolute translate-x-3 -translate-y-2 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold text-white rounded-full"
                        style={{ backgroundColor: tab.id === 'inbox' ? 'var(--theme-error-color)' : 'var(--theme-muted-text)' }}
                      >
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </button>,
                  collapsed
                )}
              </React.Fragment>
            );
          })}

          {/* Favoritos como pasta (mockup) — realoca o toggle de estrela */}
          {onToggleStarred && withTooltip(
            'Favoritos',
            <button
              onClick={onToggleStarred}
              className={`flex items-center w-full rounded-[9px] text-sm transition-colors ${collapsed ? 'justify-center' : 'gap-[11px] text-left'} ${showOnlyStarred ? 'font-semibold' : 'font-medium'}`}
              style={{
                height: 38,
                padding: collapsed ? 0 : '0 11px',
                backgroundColor: showOnlyStarred ? 'var(--theme-primary-background)' : 'transparent',
                color: showOnlyStarred ? 'var(--theme-primary)' : 'var(--theme-sidebar-text)',
              }}
              onMouseEnter={(e) => {
                if (!showOnlyStarred) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                if (!showOnlyStarred) e.currentTarget.style.backgroundColor = 'transparent';
              }}
              data-testid="email-folder-starred"
            >
              <Icon name="star" className="w-4 h-4 flex-none" style={{ color: showOnlyStarred ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }} />
              {!collapsed && <span className="flex-1 min-w-0 truncate">Favoritos</span>}
            </button>,
            collapsed
          )}
        </nav>

        {/* Colapsar rail (escondido quando o auto-colapso por largura está ativo) */}
        {!disableToggle && (
          <Tooltip content={collapsed ? 'Expandir' : 'Recolher'} side="bottom">
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center flex-none border-t transition-colors"
              style={{ height: 40, borderColor: 'var(--theme-sidebar-border)', color: 'var(--theme-sidebar-text)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              data-testid="email-folders-toggle"
            >
              <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* ═══ Lista `.em-list` (380px; mobile: full-width) ═══ */}
      <div
        className="flex flex-col h-full min-h-0 border-r flex-1 w-full min-w-0 lg:flex-none lg:w-[380px]"
        style={{
          backgroundColor: 'var(--theme-sidebar-background)',
          borderColor: 'var(--theme-sidebar-border)',
        }}
      >
        {/* `.em-lhead`: título da pasta 16 bold + Atualizar (refresh).
            Mobile: título é dropdown de pastas + botão Escrever (compose do appbar do mockup) */}
        <div
          className="flex-none flex items-center gap-2.5 border-b"
          style={{ height: HEADER_HEIGHT, padding: '0 14px', borderColor: 'var(--theme-sidebar-border)' }}
        >
          <span className="hidden lg:block flex-1 min-w-0 truncate font-bold text-[16px] tracking-tight" style={{ color: 'var(--theme-sidebar-text)' }}>
            {showOnlyStarred ? 'Favoritos' : (TABS.find((t) => t.id === activeTab)?.label ?? 'E-mails')}
          </span>
          {/* Mobile: só o seletor de pasta. O título "E-mail" saiu daqui — o
              shell já imprime a appbar com o título da rota, e repetir custava
              ~110px de cromo antes do primeiro e-mail num celular. */}
          <div className="flex-1 min-w-0 lg:hidden">
            <button
              onClick={() => setMobileFoldersOpen(true)}
              className="inline-flex items-center gap-1.5 max-w-full h-11 text-[15px] font-bold leading-none tracking-tight"
              style={{ color: 'var(--theme-sidebar-text)' }}
              aria-haspopup="listbox"
              aria-expanded={mobileFoldersOpen}
              data-testid="email-mobile-folder-picker"
            >
              <span className="truncate">
                {(showOnlyStarred ? 'Favoritos' : (TABS.find((t) => t.id === activeTab)?.label ?? 'E-mails'))}
                {(() => {
                  const c = folderCounts?.[activeTab] ?? (activeTab === 'inbox' ? unreadCount : 0);
                  return c > 0 ? ` · ${c > 99 ? '99+' : c}` : '';
                })()}
              </span>
              <Icon name="chevron-down" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
            </button>
          </div>

          {/* Escrever — mobile (compose do appbar do mockup) */}
          <button
            onClick={onCompose}
            className="lg:hidden w-11 h-11 rounded-[10px] flex items-center justify-center flex-none text-white"
            style={{ backgroundColor: 'var(--theme-button-primary)' }}
            aria-label="Escrever"
            data-testid="email-compose-mobile"
          >
            <Icon name="edit" className="w-4 h-4" />
          </button>
          {onRefresh && (
            <Tooltip content="Atualizar" side="bottom">
              <button
                onClick={onRefresh}
                className="w-8 h-8 rounded-lg hidden lg:flex items-center justify-center transition-colors flex-none"
                style={{ color: 'var(--theme-sidebar-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                data-testid="email-refresh"
              >
                <Icon name="repeat" className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
        </div>

        {/* Busca — ToolbarSearch shared (F17-22: 38px, anel de foco, X de limpar) */}
        <div className="flex-none border-b" style={{ padding: '10px 14px', borderColor: 'var(--theme-sidebar-border)' }}>
          <ToolbarSearch
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Buscar e-mails…"
            data-testid="email-search"
          />
        </div>

        {/* Itens */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="loader" className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-muted-text)' }} />
            </div>
          ) : conversations.length === 0 ? (
            <TroiaEmptyState type="empty-folder" folder={activeTab} />
          ) : (
            conversations.map((conv, idx) => {
              const isLast = idx === conversations.length - 1;
              return (
                <div key={conv.id} ref={isLast ? lastItemRef : undefined}>
                  <TroiaEmailListItem
                    conversation={conv}
                    isSelected={selectedId === conv.id}
                    expanded
                    onClick={() => onSelect(conv.id)}
                  />
                </div>
              );
            })
          )}
          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-4">
              <Icon name="loader" className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-muted-text)' }} />
            </div>
          )}
        </div>
      </div>

      {/* Seletor de pastas do celular — ActionSheet do DS no lugar do dropdown
          ad-hoc que existia aqui (markup próprio, hex fixo e alvos de 30px). */}
      <ActionSheet
        isOpen={mobileFoldersOpen}
        onClose={() => setMobileFoldersOpen(false)}
        title="Pastas"
        testId="email-mobile-folders-sheet"
        items={mobileFolderItems}
        onSelect={(key) => {
          setMobileFoldersOpen(false);
          if (key === 'starred') {
            if (!showOnlyStarred && onToggleStarred) onToggleStarred();
            return;
          }
          const tab = TABS.find((t) => t.id === key);
          if (tab) handleTabClick(tab);
        }}
      />
    </div>
  );
};

TroiaSidebar.displayName = 'TroiaSidebar';
